import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Train, Droplets, Zap, Waves } from "lucide-react";

const CASE_STUDIES = [
    {
        icon: Train,
        title: "City Rail Link",
        description:
            "Australia and New Zealand's largest transport infrastructure project and its first urban underground rail system — showcasing how digital modelling and data integration are transforming large-scale delivery.",
        color: "#6bbac2",
    },
    {
        icon: Droplets,
        title: "Council-Scale Digital Twins",
        description:
            "Water treatment modernisation, wastewater optimisation and flood modelling initiatives across regional councils.",
        color: "#bfbe28",
    },
    {
        icon: Zap,
        title: "Electricity Distribution & Smart Metering",
        description:
            "Digital asset management and AI-driven network optimisation across energy systems.",
        color: "#6bbac2",
    },
    {
        icon: Waves,
        title: "Next-Generation Urban Water",
        description:
            "Integrated water management under Local Water Done Well reforms.",
        color: "#bfbe28",
    },
];

function CaseStudyCard({ item, index, size }) {
    const Icon = item.icon;
    const isPrimary = item.color === "#6bbac2";

    return (
        <Col key={index} lg={6} className="mb-4">
            <Card
                className="case-study-card h-100"
                style={{
                    background: "rgba(255, 255, 255, 0.04)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    borderRadius: "20px",
                    padding: 0,
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = isPrimary
                        ? "rgba(107, 186, 194, 0.1)"
                        : "rgba(191, 190, 40, 0.08)";
                    e.currentTarget.style.borderColor = isPrimary
                        ? "rgba(107, 186, 194, 0.45)"
                        : "rgba(191, 190, 40, 0.45)";
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = isPrimary
                        ? "0 20px 40px rgba(107, 186, 194, 0.22)"
                        : "0 20px 40px rgba(191, 190, 40, 0.2)";
                    const iconEl = e.currentTarget.querySelector(".case-study-icon-wrap");
                    if (iconEl) {
                        iconEl.style.transform = "scale(1.08) rotate(-2deg)";
                        iconEl.style.boxShadow = isPrimary
                            ? "0 10px 24px rgba(107, 186, 194, 0.35)"
                            : "0 10px 24px rgba(191, 190, 40, 0.35)";
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    const iconEl = e.currentTarget.querySelector(".case-study-icon-wrap");
                    if (iconEl) {
                        iconEl.style.transform = "scale(1) rotate(0deg)";
                        iconEl.style.boxShadow = "none";
                    }
                }}
            >
                <div
                    style={{
                        padding: size ? "1.75rem" : "1.5rem",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}
                >
                    <div
                        className="case-study-icon-wrap"
                        style={{
                            width: size ? 64 : 56,
                            height: size ? 64 : 56,
                            borderRadius: "16px",
                            background: `linear-gradient(145deg, ${item.color}28, ${item.color}12)`,
                            border: `2px solid ${item.color}50`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "1.25rem",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                    >
                        <Icon
                            size={size ? 32 : 28}
                            strokeWidth={2}
                            style={{ color: item.color }}
                        />
                    </div>
                    <h3
                        className="text-white mb-2"
                        style={{
                            fontSize: size ? "1.25rem" : "1.1rem",
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                            lineHeight: "1.3",
                        }}
                    >
                        {item.title}
                    </h3>
                    <p
                        className="text-white mb-0"
                        style={{
                            fontSize: size ? "1rem" : "0.92rem",
                            lineHeight: "1.65",
                            opacity: 0.9,
                            fontWeight: 400,
                        }}
                    >
                        {item.description}
                    </p>
                </div>
                <div
                    style={{
                        height: "4px",
                        width: "100%",
                        background: `linear-gradient(90deg, ${item.color}, transparent)`,
                        opacity: 0.6,
                    }}
                />
            </Card>
        </Col>
    );
}

function FeaturedCaseStudiesSection() {
    const size =
        document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

    return (
        <section
            className="section py-5"
            style={{
                background: "transparent",
                position: "relative",
                zIndex: 1,
            }}
        >
            <Container>
                <Row className="justify-content-center mb-5">
                    <Col className="text-center">
                        <h2
                            className="text-white mb-0"
                            style={{
                                fontSize: size ? "2.25rem" : "1.75rem",
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                lineHeight: "1.2",
                                textTransform: "uppercase",
                                color: "#6bbac2",
                            }}
                        >
                            Featured Case Studies &amp; Projects
                        </h2>
                        <div
                            style={{
                                width: "100px",
                                height: "4px",
                                background: "linear-gradient(90deg, #6bbac2, #bfbe28)",
                                borderRadius: "2px",
                                margin: "1rem auto 0",
                            }}
                        />
                    </Col>
                </Row>
                <Row className="justify-content-center mb-5">
                    <Col xs={12}>
                        <img
                            src={require("assets/titleimages/6.jpg")}
                            alt="Featured projects"
                            style={{
                                width: "100%",
                                height: "auto",
                                maxHeight: "300px",
                                objectFit: "cover",
                                borderRadius: "16px",
                                border: "1px solid rgba(255,255,255,0.1)",
                                boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
                            }}
                        />
                    </Col>
                </Row>
                <Row>
                    {CASE_STUDIES.map((item, i) => (
                        <CaseStudyCard key={i} item={item} index={i} size={size} />
                    ))}
                </Row>
            </Container>
        </section>
    );
}

export default FeaturedCaseStudiesSection;
